import { Fragment, useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { cx } from "@/lib/cx";
import { FlowsIcon, InboxIcon, KanbanIcon, TasksIcon } from "@/ui/icons";
import { SearchField } from "@/ui/SearchField";
import { LAYER } from "@/ui/overlay/surface";
import { popperMaterial } from "@/ui/overlay/Menu";
import { useBackClose } from "@/ui/overlay/useBackClose";
import { useCloseOnlyOutside } from "@/ui/overlay/useCloseOnlyOutside";
import { TONE_TEXT, type Tone } from "@/ui/tones";

import { useSearch } from "./useSearch";
import type { SearchResult, SearchResultKind } from "./search";

/**
 * The dashboard's universal search — one field over every surface.
 *
 * It is the only control on a screen that is otherwise read-only, and it earns
 * the exception because it is a *reading* affordance: type a few letters and go
 * straight to the project, inbox item, task or card you meant, wherever it
 * lives. Matching and ranking are {@link useSearch}'s; this component is the
 * field, the results panel, and the keyboard.
 *
 * ## Behaviour
 *
 * - The panel opens on focus and only once something is typed; a blank field is
 *   just a field.
 * - ↑/↓ move the highlight, Return opens the highlighted row, Escape clears then
 *   closes. The mouse and the keyboard share one `active` index so they never
 *   disagree about what Return will do.
 * - Choosing a result navigates and empties the field. A project opens its
 *   board; the other three open their surface with the item already peeked —
 *   the `?peek=` deep link `usePeekParam` reads on the far side.
 * - It is a proper `combobox`: `aria-expanded`, `aria-activedescendant` and
 *   `role="option"` rows, so it is operable and announced without sight.
 */
export type SearchBarProps = {
  /** Placement classes for the field and its anchored result surface. */
  className?: string;
  /** Geometry for the field itself; the result list keeps its own Batch 5 styling. */
  inputClassName?: string;
};

export function SearchBar({ className, inputClassName }: SearchBarProps = {}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const { results, ready } = useSearch(query);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const trimmed = query.trim();
  const showPanel = open && trimmed !== "";

  // A fresh result set always highlights the top row — the one the kind order
  // put first — rather than stranding the cursor at a stale index.
  useEffect(() => {
    setActive(0);
  }, [results]);

  // A click anywhere off the field closes the panel, and only closes it — it
  // used to pass through to whatever was underneath.
  useCloseOnlyOutside(showPanel, () => setOpen(false), rootRef);
  // The phone's Back folds the results away like an outside tap does; the
  // query stays, so focusing the field again brings them back.
  useBackClose(showPanel, () => {
    setOpen(false);
    inputRef.current?.blur();
  });

  const closeResults = useCallback(() => {
      setQuery("");
      setOpen(false);
      inputRef.current?.blur();
  }, []);

  const go = useCallback(
    (result: SearchResult) => {
      closeResults();
      navigate(result.to);
    },
    [closeResults, navigate],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape") {
        if (trimmed !== "") {
          setQuery("");
        } else {
          setOpen(false);
          inputRef.current?.blur();
        }
        return;
      }
      if (!showPanel || results.length === 0) {
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActive((index) => (index + 1) % results.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActive((index) => (index - 1 + results.length) % results.length);
      } else if (event.key === "Enter") {
        const result = results[active];
        if (result) {
          event.preventDefault();
          go(result);
        }
      }
    },
    [trimmed, showPanel, results, active, go],
  );

  const activeId = showPanel && results[active] ? `${listId}-${active}` : undefined;

  return (
    <div ref={rootRef} className={cx("relative", className)}>
      <SearchField
        ref={inputRef}
        value={query}
        onValueChange={setQuery}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        variant="plain"
        label="Search projects, Flows, inbox, tasks and cards"
        placeholder="Search projects, Flows, inbox, tasks, cards…"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        inputClassName={inputClassName}
      />

      {showPanel ? (
        <div
          className={cx(
            popperMaterial,
            LAYER.overlay,
            "ps1-search-panel absolute inset-x-0 top-[calc(100%+0.25rem)] max-h-[60vh] overflow-y-auto p-1",
          )}
        >
          {results.length === 0 ? (
            <p className="px-2 py-3 text-center text-caption text-muted">
              {ready ? "No matches." : "Searching…"}
            </p>
          ) : (
            <ul id={listId} role="listbox" aria-label="Search results" className="flex flex-col">
              {results.map((result, index) => (
                <Fragment key={`${result.kind}:${result.id}`}>
                  {index === 0 || results[index - 1]?.kind !== result.kind ? (
                    <li role="presentation" className="ps1-search-kind px-2 pt-2 pb-1 text-caption uppercase text-subtle">
                      {KIND_META[result.kind].label}
                    </li>
                  ) : null}
                  <li role="presentation">
                  <ResultRow
                    id={`${listId}-${index}`}
                    result={result}
                    active={index === active}
                    onPointerMove={() => setActive(index)}
                    onSelect={closeResults}
                  />
                  </li>
                </Fragment>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

type KindMeta = { icon: ReactNode; tone: Tone; label: string };

/**
 * Per-kind icon, tone and label. The icons are the navigation glyphs — a result
 * row and the surface it opens are the same object seen twice — and the tone is
 * fixed per kind, carrying identity rather than urgency.
 */
const KIND_META: Record<SearchResultKind, KindMeta> = {
  project: { icon: <KanbanIcon className="size-4" />, tone: "plan", label: "Project" },
  flow: { icon: <FlowsIcon className="size-4" />, tone: "accent", label: "Flows" },
  inbox: { icon: <InboxIcon className="size-4" />, tone: "focus", label: "Inbox" },
  task: { icon: <TasksIcon className="size-4" />, tone: "accent", label: "Task" },
  card: { icon: <KanbanIcon className="size-4" />, tone: "plan", label: "Card" },
};

function ResultRow({
  id,
  result,
  active,
  onPointerMove,
  onSelect,
}: {
  id: string;
  result: SearchResult;
  active: boolean;
  onPointerMove: () => void;
  onSelect: () => void;
}) {
  const meta = KIND_META[result.kind];
  return (
    <Link
      id={id}
      to={result.to}
      role="option"
      aria-selected={active}
      // `onMouseDown` preventDefault keeps the input focused through the click,
      // so the panel's blur-close does not race the navigation.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onSelect}
      onPointerMove={onPointerMove}
      className={cx(
        // `row-height`: a result's title wraps to two lines on a phone, and a
        // fixed height would hide the second one.
        "ps1-search-option flex row-height w-full items-center gap-2 rounded-control px-2 py-1 text-left",
        active ? "bg-fill-2" : "bg-transparent",
      )}
    >
      <span
        aria-hidden
        className={cx(
          "flex size-4 shrink-0 items-center justify-center",
          TONE_TEXT[meta.tone],
        )}
      >
        {meta.icon}
      </span>

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="title-clamp text-body text-ink">{result.title || "Untitled"}</span>
        {result.subtitle ? (
          <span className="truncate text-caption text-muted">{result.subtitle}</span>
        ) : null}
      </span>
    </Link>
  );
}
